/**
 * AIChat · 容器宽度观测 hook
 *
 * 对应文档：docs/AI-Photo-Category/TECH_PLAN.md §4.2.1
 *
 * 职责：
 *  · 通过 ResizeObserver 监听挂载节点的宽度变化
 *  · 根据宽度派生 SizeClass（compact / standard / wide），供组件内部与 Context 消费
 *
 * 设计决策：
 *  · 用 useEffect（非 useLayoutEffect）：首期不涉及阻塞布局的同步读写，
 *    挂载后立即 measure 一次可避免 sizeClass 从默认值飘向真实值时的闪烁
 *  · 首次 measure 同步一次 getBoundingClientRect，避免等待 observer 首帧
 *  · 不做节流：性能足够；未来集成拖拽出现问题再加 rAF
 *  · ResizeObserver 缺失时保持初始值不抛错，目标浏览器（现代 Chrome / Safari）均支持
 */

import { useEffect, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import type { SizeClass } from '../../../../types/ai-classify'

/**
 * 根据容器像素宽度计算 SizeClass · 纯函数
 *
 * 断点与文档保持同步，修改时同步更新 TECH_PLAN §4.2.1：
 *  · ≤ 480px   → compact
 *  · 481~720px → standard
 *  · > 720px   → wide
 */
export function computeSizeClass(width: number): SizeClass {
  if (width <= 480) return 'compact'
  if (width <= 720) return 'standard'
  return 'wide'
}

interface UseContainerWidthResult<T extends HTMLElement> {
  /** 需要挂到目标 DOM 节点的 ref */
  ref: MutableRefObject<T | null>
  /** 最新像素宽度 · 未测量前为 0 */
  width: number
  /** 派生 SizeClass · 未测量前返回 'standard'，防止初始 compact 闪烁 */
  sizeClass: SizeClass
}

/**
 * 监听目标 DOM 节点的宽度变化并派生 SizeClass
 *
 * 使用方式：
 * ```tsx
 * const { ref, width, sizeClass } = useContainerWidth<HTMLDivElement>()
 * return <div ref={ref} data-size-class={sizeClass}>...</div>
 * ```
 */
export function useContainerWidth<
  T extends HTMLElement = HTMLElement
>(): UseContainerWidthResult<T> {
  const ref = useRef<T | null>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // 首次同步 measure · 避免等待 observer 第一次回调
    setWidth(el.getBoundingClientRect().width)

    // 浏览器不支持时降级：仅保留首次宽度，不再订阅变化
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width)
      }
    })
    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [])

  // 未测量前（width === 0）回退到 'standard'，避免首帧按 compact 布局
  const sizeClass: SizeClass = width > 0 ? computeSizeClass(width) : 'standard'

  return { ref, width, sizeClass }
}
